<template>
  <div class="gallery-light-comparison" :style="{'--reveal':`${reveal}%`}">
    <img :src="dark" :alt="alt" width="1920" height="1046" draggable="false" @load="imageReady" />
    <div class="comparison-light"><img :src="light" alt="" width="1920" height="1046" draggable="false" @load="imageReady" /></div>
    <div class="comparison-divider" aria-hidden="true"><span><svg viewBox="0 0 24 24"><path d="m9 8-4 4 4 4m6-8 4 4-4 4"/></svg></span></div>
    <input v-if="interactive" v-model.number="reveal" class="comparison-input" type="range" min="0" max="100" step="1" aria-label="Compare light and dark Gallery lighting" :aria-valuetext="`${Math.round(reveal)} percent light, ${100-Math.round(reveal)} percent dark`" @pointerdown="takeControl" @keydown="takeControl" @focus="takeControl" />
  </div>
</template>
<script>
export default {
  props: {interactive: {type:Boolean,default:true}, light: {type:String,required:true}, dark: {type:String,required:true}, alt: String},
  data: () => ({reveal:50}),
  mounted() {
    this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._motion.addEventListener('change', this.syncPreview);
    this._observer = new IntersectionObserver(([entry]) => {
      this._visible = entry.isIntersecting;
      this.syncPreview();
    }, {threshold:.5});
    this._observer.observe(this.$el);
    document.addEventListener('visibilitychange', this.syncPreview);
    this.imageReady();
  },
  beforeUnmount() {
    this.stopPreview();
    this._observer?.disconnect();
    this._motion?.removeEventListener('change', this.syncPreview);
    document.removeEventListener('visibilitychange', this.syncPreview);
  },
  methods: {
    imageReady() {
      this._ready = [...this.$el.querySelectorAll('img')].every(img => img.complete && img.naturalWidth);
      this.syncPreview();
    },
    stopPreview() { cancelAnimationFrame(this._frame); this._frame = null; },
    takeControl() { this._interacted = true; this.stopPreview(); },
    syncPreview() {
      this.stopPreview();
      if (!this._ready || !this._visible || this._interacted || this._motion?.matches || document.hidden) return;
      // One quiet sweep introduces the divider. The visitor owns it as soon
      // as they focus or touch it, and it rests at an even split afterwards.
      let previous;
      const tick = now => {
        this._elapsed = (this._elapsed || 0) + (previous ? now - previous : 0);
        previous = now;
        const progress = Math.min(1, this._elapsed / 4800);
        this.reveal = 50 + 32 * Math.sin(progress * Math.PI * 2) * Math.sin(progress * Math.PI);
        if (progress < 1) this._frame = requestAnimationFrame(tick);
        else { this.reveal = 50; this._interacted = true; this._frame = null; }
      };
      this._frame = requestAnimationFrame(tick);
    },
  },
};
</script>
<style scoped>
.gallery-light-comparison{position:relative;overflow:hidden;isolation:isolate;aspect-ratio:1920/1046}
img{display:block;width:100%;height:100%;object-fit:cover;user-select:none;transform:scale(var(--gallery-image-scale,1));transition:transform 420ms cubic-bezier(.22,1,.36,1)}
.comparison-light{position:absolute;inset:0;clip-path:inset(0 calc(100% - var(--reveal)) 0 0)}
.comparison-divider{position:absolute;left:var(--reveal);top:0;bottom:0;width:1px;background:#fff;box-shadow:0 0 2px #0008;pointer-events:none}
.comparison-divider>span{position:absolute;left:0;top:50%;display:grid;place-items:center;width:40px;height:40px;transform:translate(-50%,-50%);border:1px solid #fff9;border-radius:50%;color:#fff;background:#292623d9;box-shadow:0 2px 12px #0002}
.comparison-divider svg{width:24px;height:24px;fill:none;stroke:currentColor;stroke-width:1.2;stroke-linecap:round;stroke-linejoin:round}
.comparison-input{position:absolute;z-index:3;left:-22px;top:0;width:calc(100% + 44px);height:100%;margin:0;padding:0;opacity:0;appearance:none;cursor:ew-resize;touch-action:pan-y}
.comparison-input::-webkit-slider-thumb{appearance:none;width:44px;height:44px}
.comparison-input::-moz-range-thumb{width:44px;height:44px;border:0}
.gallery-light-comparison:has(input:focus-visible) .comparison-divider>span{outline:2px solid #fff;outline-offset:3px}
@media(max-width:600px){.comparison-divider>span{width:34px;height:34px}}
@media(prefers-reduced-motion:reduce){img{transition:none}}
</style>
